import { Injectable } from '@angular/core';
import { RestApiService } from './rest-api.service';
import { HttpClient } from '@angular/common/http';
import {
  Subscriber,
  DataCount,
  SubscriberFilter,
  SubscriberCreate,
  SubscriptionPlan,
  Subscription,
} from 'src/models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SubscriberService extends RestApiService {
  //Variáveis
  private _urlSubscribers = '/subscribers';
  private _urlSubscriber = '/subscribers/:subscriberId';
  private _urlSubscribersCount = '/subscribers/count';

  constructor(public http: HttpClient) {
    super(http);
  }

  ////////////////////////////
  //////////////////// ASSINANTES
  ////////////////////////////
  getSubscribers(filter?: SubscriberFilter) {
    let url = this._urlSubscribers;
    if (filter) {
      url += this.queryParamsToUrl(filter);
      return this.get(url, Subscriber) as Observable<Subscriber[]>;
    }
    return this.get(url, Subscriber) as Observable<Subscriber[]>;
  }

  count(filter?: SubscriberFilter) {
    let url = this._urlSubscribersCount;
    if (filter) {
      url += this.queryParamsToUrl(filter);
      return this.get(url, DataCount) as Observable<DataCount>;
    }
    return this.get(url, DataCount) as Observable<DataCount>;
  }

  createSubscriber(subscriber: SubscriberCreate) {
    const url = this._urlSubscribers;
    return this.post(url, subscriber, SubscriberCreate);
  }

  getSubscriber(subscriberId: number) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.get(url, Subscriber) as Observable<Subscriber>;
  }

  updateSubscriber(subscriber: Subscriber) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriber.id.toString()
    );
    return this.put(url, subscriber, Subscriber);
  }

  deleteSubscriber(subscriber: Subscriber) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriber.id.toString()
    );
    return this.del(url, Subscriber);
  }

  // obtem a lista de assinaturas de um assinante
  getSubscriptions(subscriberId: number) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.get(url + '/subscriptions', Subscription) as Observable<Subscription[]>
  }

  // adiciona uma assinatura a um assinante
  addSubscription(subscriberId: number, subscription: { planId: number, startDate: Date | string, endDate: Date | string, sendEmail: boolean }) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.postData(url + '/add-subscription', subscription);
  }

  // edita uma assinatura
  updateSubscription(subscriberId: number, subscription: { id: number, planId: number, startDate: Date | string, endDate: Date | string}) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.putData(url + '/subscriptions/' + subscription.id, subscription);
  }

  activateSubscription(subscriberId: number, subscriptionId: number) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.postData(url + '/subscriptions/' + subscriptionId + '/activate');
  }

  cancelSubscription(subscriberId: number, subscriptionId: number) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.postData(url + '/subscriptions/' + subscriptionId + '/cancel');
  }

  // obter lista de planos
  getPlans() {
    return this.get('/subscription-plans', SubscriptionPlan) as Observable<
      SubscriptionPlan[]
    >;
  }

  // reenviar email de cadastro
  resendRegistrationEmail(subscriberId: number) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.post(
      url + '/resend-registration-email',
      new Subscriber({ id: subscriberId }),
      Subscriber
    );
  }

  // reenviar email de cadastro para múltiplos assinantes
  resendRegistrationEmailMultiple(subscribersIds: number[]) {
    return this.postData<string[]>(
      this._urlSubscribers + '/resend-registration-email-multiple',
      { subscribersIds }
    );
  }

  // resetar senha de assinante
  resetPassword(subscriberId: number, sendEmail: boolean) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.postData<{ resetPasswordLink: string }>(
      url + '/reset-password?sendEmail=' + sendEmail.toString()
    );
  }

  // copiar link de cadastro
  copyRegistrationLink(subscriberId: number) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.getData<{ registeredlink: string }>(
      url + '/copy-registration-link'
    );
  }

  // gerar token de acesso
  generateToken(subscriberId: number) {
    const url = this._urlSubscriber.replace(
      ':subscriberId',
      subscriberId.toString()
    );
    return this.postData<{ token: string }>(url + '/generate-access-token');
  }

  // enviar arquivo
  sendCsv(file: File) {
    return this.sendFile<{
      created: { registeredlink: string; name: string; email: string }[];
      failed: {
        name: string;
        document: string;
        phone: string;
        email: string;
        planName: string;
        recurrencyPeriod: number;
        maxChargeCycles: number;
        price: number;
        paymentType: string;
        error:
        | { message: string; _statusCode: number; _type: string }[]
        | { message: string; _statusCode: number; _type: string };
      }[];
    }>('/subscribers/bulk', file);
  }

  private queryParamsToUrl(filter: SubscriberFilter) {
    let url = '';
    if (filter) {
      url += '?';
      const copyParams: any = { ...filter };
      for (const param of Object.keys(copyParams)) {
        if (copyParams[param] != undefined && copyParams[param] != '') {
          url += `${param}=${copyParams[param]}&`;
        }
      }
    }
    return url;
  }
}
